export const SITE_URL =
  import.meta.env.VITE_SITE_URL || 'https://allchinabuyspread.cyou';

export const SITE_NAME = 'AllChinaBuy Spreadsheet Resource Hub';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

export type PageType =
  | 'home'
  | 'article'
  | 'products'
  | 'categories'
  | 'guides'
  | 'review'
  | 'shipping'
  | 'coupon'
  | 'safety'
  | 'faq'
  | 'about'
  | 'contact'
  | 'legal'
  | 'notfound';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  indexable: boolean;
  type: PageType;
  lang: string;
  section: string;
}

const HOME_SEO = {
  title: 'AllChinaBuy Spreadsheet — Find Products, Sellers & Buying Resources',
  description:
    'Explore the AllChinaBuy spreadsheet, product lists, seller resources, QC guides, shipping information and practical buying tips — all organized in one easy-to-use resource hub.',
  keywords:
    'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse, AllChinaBuy legit, AllChinaBuy QC',
};

const SECTIONS: Record<
  string,
  { title: string; description: string; keywords: string; type: PageType }
> = {
  spreadsheet: {
    title: 'AllChinaBuy Spreadsheet: Complete Product & Seller Guide 2026',
    description:
      'The definitive AllChinaBuy spreadsheet guide for 2026. Learn how to find products, sellers, QC photos, shipping info and buying tips using the AllChinaBuy spreadsheet effectively.',
    keywords:
      'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet guide, AllChinaBuy product spreadsheet, AllChinaBuy seller spreadsheet, AllChinaBuy spreadsheet 2026',
    type: 'article',
  },
  products: {
    title: 'AllChinaBuy Products — Browse the Full Product Catalog',
    description:
      'Browse the complete AllChinaBuy product catalog across shoes, hoodies, t-shirts, jackets, bags, accessories and more. Click any item to view details and purchase on kaspreadsheet.com.',
    keywords:
      'AllChinaBuy products, AllChinaBuy product catalog, AllChinaBuy product list, AllChinaBuy shoes, AllChinaBuy clothing, AllChinaBuy bags',
    type: 'products',
  },
  categories: {
    title: 'AllChinaBuy Categories — Shoes, Clothing, Bags & More',
    description:
      'Explore all AllChinaBuy product categories including shoes, hoodies, t-shirts, jackets, pants, headwear, sets, bags, belts, perfumes and accessories organized for easy browsing.',
    keywords:
      'AllChinaBuy categories, AllChinaBuy product categories, AllChinaBuy shoes, AllChinaBuy hoodies, AllChinaBuy bags',
    type: 'categories',
  },
  guides: {
    title: 'AllChinaBuy Guides & Tutorials — Learn to Buy Safely',
    description:
      'Step-by-step AllChinaBuy guides covering the spreadsheet, product research, seller verification, QC photos, shipping costs, coupons and beginner buying tips.',
    keywords:
      'AllChinaBuy guides, AllChinaBuy tutorial, AllChinaBuy how to, AllChinaBuy spreadsheet guide, AllChinaBuy buying guide',
    type: 'guides',
  },
  review: {
    title: 'AllChinaBuy Review 2026 — Is It Legit & Worth It?',
    description:
      'An independent AllChinaBuy review for 2026 covering the spreadsheet, product discovery, QC photos, shipping, warehouse and whether the platform is legit and safe.',
    keywords:
      'AllChinaBuy review, AllChinaBuy review 2026, is AllChinaBuy legit, AllChinaBuy worth it',
    type: 'review',
  },
  shipping: {
    title: 'AllChinaBuy Shipping Cost Guide — Methods, Prices & Tips',
    description:
      'Complete AllChinaBuy shipping guide covering shipping methods, cost calculation, package consolidation, delivery times and tips to reduce international shipping costs.',
    keywords:
      'AllChinaBuy shipping, AllChinaBuy shipping cost, AllChinaBuy shipping methods, AllChinaBuy shipping time, AllChinaBuy warehouse',
    type: 'shipping',
  },
  coupon: {
    title: 'AllChinaBuy Coupon & Discount Codes 2026',
    description:
      'Find the latest AllChinaBuy coupon codes, discount codes and promo strategies to save on service fees and shipping in 2026.',
    keywords:
      'AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy promo, AllChinaBuy promo code, AllChinaBuy deals',
    type: 'coupon',
  },
  safety: {
    title: 'Is AllChinaBuy Safe? Legitimacy & Safety Guide',
    description:
      'Learn whether AllChinaBuy is safe and legit, including payment safety, seller verification, QC checks and how to avoid common scams when buying from China.',
    keywords:
      'AllChinaBuy safe, is AllChinaBuy safe, AllChinaBuy legit, AllChinaBuy scam, AllChinaBuy safety',
    type: 'safety',
  },
  faq: {
    title: 'AllChinaBuy FAQ — Frequently Asked Questions',
    description:
      'Answers to the most common AllChinaBuy questions about the spreadsheet, safety, shipping, coupons, seller verification and the buying process.',
    keywords:
      'AllChinaBuy FAQ, AllChinaBuy questions, AllChinaBuy help, AllChinaBuy support',
    type: 'faq',
  },
  about: {
    title: 'About Us — AllChinaBuy Spreadsheet Resource Hub',
    description:
      'Learn about the AllChinaBuy Spreadsheet Resource Hub, our mission, editorial standards and how we help buyers research products, sellers and shipping.',
    keywords:
      'about AllChinaBuy spreadsheet, AllChinaBuy resource hub, AllChinaBuy about',
    type: 'about',
  },
  contact: {
    title: 'Contact Us — AllChinaBuy Spreadsheet Resource Hub',
    description:
      'Get in touch with the AllChinaBuy Spreadsheet Resource Hub team with questions, feedback or corrections about our guides and resources.',
    keywords:
      'contact AllChinaBuy, AllChinaBuy contact, AllChinaBuy spreadsheet contact',
    type: 'contact',
  },
  privacy: {
    title: 'Privacy Policy — AllChinaBuy Spreadsheet Resource Hub',
    description:
      'Read the privacy policy for the AllChinaBuy Spreadsheet Resource Hub covering data collection, cookies and your rights.',
    keywords: 'AllChinaBuy privacy policy, privacy policy',
    type: 'legal',
  },
  terms: {
    title: 'Terms of Service — AllChinaBuy Spreadsheet Resource Hub',
    description:
      'Read the terms of service governing use of the AllChinaBuy Spreadsheet Resource Hub website and its content.',
    keywords: 'AllChinaBuy terms of service, terms',
    type: 'legal',
  },
  disclaimer: {
    title: 'Disclaimer — AllChinaBuy Spreadsheet Resource Hub',
    description:
      'Read the disclaimer for the AllChinaBuy Spreadsheet Resource Hub, an independent informational resource not affiliated with AllChinaBuy.',
    keywords: 'AllChinaBuy disclaimer, disclaimer',
    type: 'legal',
  },
};

const VALID_LANGS = ['en', 'fr', 'da', 'de'];

function buildHome(lang: string): SeoData {
  return {
    title: HOME_SEO.title,
    description: HOME_SEO.description,
    keywords: HOME_SEO.keywords,
    canonical: lang ? `${SITE_URL}/${lang}/` : `${SITE_URL}/`,
    indexable: true,
    type: 'home',
    lang: lang || 'en',
    section: '',
  };
}

function buildNotFound(): SeoData {
  return {
    title: 'Page Not Found',
    description: '',
    keywords: '',
    canonical: '',
    indexable: false,
    type: 'notfound',
    lang: 'en',
    section: '',
  };
}

export function getSeoData(pathname: string): SeoData {
  const clean = pathname.replace(/^\/+|\/+$/g, '');
  if (!clean) {
    return buildHome('');
  }
  const parts = clean.split('/');
  const lang = parts[0];
  if (parts.length === 1) {
    return buildHome(VALID_LANGS.includes(lang) ? lang : '');
  }
  const section = parts[1];
  const conf = SECTIONS[section];
  if (!conf) {
    return buildNotFound();
  }
  const resolvedLang = VALID_LANGS.includes(lang) ? lang : 'en';
  return {
    title: conf.title,
    description: conf.description,
    keywords: conf.keywords,
    canonical: `${SITE_URL}/${clean}/`,
    indexable: true,
    type: conf.type,
    lang: resolvedLang,
    section,
  };
}